<template>
  <v-container fluid class="game-container pa-0 ma-0">
    <!-- Hero header (always shown when leaderboard is enabled) -->
    <v-container v-if="localSeeScore" fluid class="leader-page px-3 px-md-4 pt-3 pt-md-4 pb-6">
      <div v-if="showHeroBanner" class="leader-stack-block header-section customBorderRadius mb-4 mb-md-5">
        <v-btn
          icon
          small
          class="header-close-btn"
          aria-label="Close leaderboard banner"
          @click="dismissHeader"
        >
          <v-icon color="white">mdi-close</v-icon>
        </v-btn>
        <v-row align="start" class="pa-0 ma-0 header-top-row">
          <v-col cols="12" md="7" class="d-flex align-center header-title-col">
            <div class="header-icon-wrapper">
              <v-icon size="32" color="white">mdi-trophy</v-icon>
            </div>
            <div class="ml-3 ml-md-4">
              <h1 class="page-title">Leaderboard</h1>
              <p class="page-subtitle mb-0">
                Top performers and most active team members
              </p>
            </div>
          </v-col>
          <v-col cols="12" md="5" class="header-actions-col mt-3 mt-md-0">
            <div class="header-actions-inner">
              <div class="header-chips-row">
                <v-chip class="feature-chip" outlined small>
                  <v-icon left x-small>mdi-medal</v-icon>
                  Rankings
                </v-chip>
                <v-chip class="feature-chip" outlined small>
                  <v-icon left x-small>mdi-chart-line</v-icon>
                  Performance
                </v-chip>
              </div>
            </div>
          </v-col>
        </v-row>
        <div class="header-bottom mt-4">
          <div class="feature-pills-container">
            <div class="feature-pill">
              <v-icon small class="mr-2" color="white">mdi-phone-outgoing</v-icon>
              Top callers
            </div>
            <div class="feature-pill">
              <v-icon small class="mr-2" color="white">mdi-account-clock</v-icon>
              Most active
            </div>
            <div class="feature-pill">
              <v-icon small class="mr-2" color="white">mdi-calendar-range</v-icon>
              Time based
            </div>
            <div class="feature-pill">
              <v-icon small class="mr-2" color="white">mdi-tag-multiple</v-icon>
              Disposition scoring
            </div>
          </div>
          <div class="header-toggle-row">
            <span class="header-toggle-label">Show leaderboard</span>
            <v-switch
              v-model="localSeeScore"
              class="header-switch ma-0 pa-0"
              inset
              dense
              hide-details
              dark
              color="white"
              @change="ScoreVisible"
            />
          </div>
        </div>
      </div>

      <div class="leader-stack-block">
        <v-card class="modern-card customBorderRadius elevation-0 leader-main-card">
            <div class="card-header-green" />
            <v-card-text class="pa-0">
              <v-tabs
                v-model="tabs"
                class="leader-tabs"
                color="primary"
                background-color="transparent"
                centered
                grow
                show-arrows
              >
                <v-tab href="#topCaller">
                  <v-icon left small>mdi-phone-outgoing</v-icon>
                  <span class="d-none d-sm-inline">Top caller</span>
                  <span class="d-inline d-sm-none">Calls</span>
                </v-tab>
                <v-tab href="#topActive">
                  <v-icon left small>mdi-timer-outline</v-icon>
                  <span class="d-none d-sm-inline">Most active</span>
                  <span class="d-inline d-sm-none">Active</span>
                </v-tab>
              </v-tabs>

              <v-divider />

              <div class="leader-filters pa-4 pb-2 pt-3">
                <v-row dense>
                  <v-col cols="12" sm="6" class="leader-filter-col">
                    <v-select
                      v-model="time"
                      :items="timeoptions"
                      outlined
                      label="Time period"
                      dense
                      hide-details
                      class="filter-field filter-field--control"
                      prepend-inner-icon="mdi-calendar-range"
                      @change="onDateChange"
                    />
                    <p class="filter-field-hint filter-field-hint--spacer" aria-hidden="true">
                      &nbsp;
                    </p>
                  </v-col>
                  <v-col cols="12" sm="6" class="leader-filter-col">
                    <v-autocomplete
                      v-model="rankingDispos"
                      :items="dispoOptions"
                      item-text="title"
                      item-value="title"
                      multiple
                      clearable
                      outlined
                      dense
                      hide-details
                      hide-selected
                      class="filter-field filter-field--control filter-field--dispos"
                      label="Rank by dispositions"
                      prepend-inner-icon="mdi-tag-multiple-outline"
                      @change="onRankingDisposChange"
                    >
                      <template v-slot:selection="{ item, index }">
                        <v-chip
                          v-if="index < 2"
                          :key="'dispo-sel-' + index + '-' + dispoSelectionTitle(item)"
                          close
                          small
                          class="filter-dispo-chip"
                          @click:close.stop="removeRankingDispo(item)"
                        >
                          <span class="filter-dispo-chip__text">{{
                            dispoSelectionTitle(item)
                          }}</span>
                        </v-chip>
                        <span
                          v-else-if="index === 2"
                          key="dispo-sel-more"
                          class="filter-dispo-more"
                        >
                          +{{ rankingDispos.length - 2 }}
                          {{
                            rankingDispos.length - 2 === 1 ? " other" : " others"
                          }}
                        </span>
                      </template>
                    </v-autocomplete>
                    <p class="filter-field-hint">
                      Optional — empty ranks by total calls.
                    </p>
                  </v-col>
                </v-row>
              </div>

              <v-tabs-items v-model="tabs" class="leader-tab-items">
                <v-tab-item value="topCaller" class="px-4 px-md-6 pb-6 pt-2">
                  <Leader
                    :tabtype="tabs"
                    :accounts="finalData"
                    :use-dispo-ranking="useDispoRanking"
                    :ranking-dispo-label="rankingDispoLabel"
                    :ranking-dispos="rankingDispos"
                  />
                </v-tab-item>
                <v-tab-item value="topActive" class="px-4 px-md-6 pb-6 pt-2">
                  <Leader
                    :tabtype="tabs"
                    :accounts="finalData"
                    :use-dispo-ranking="useDispoRanking"
                    :ranking-dispo-label="rankingDispoLabel"
                    :ranking-dispos="rankingDispos"
                  />
                </v-tab-item>
              </v-tabs-items>
            </v-card-text>
        </v-card>
      </div>
    </v-container>

    <!-- Score Turned Off View -->
    <v-container
      v-else
      fluid
      class="fill-height pa-0 ma-0 leaderboard-off"
    >
      <v-card width="100%" height="100vh" flat color="transparent">
        <v-img
          contain
          height="50%"
          :src="require('../assets/noscore.png')"
        ></v-img>
        <h1 class="text-center font-weight-medium mt-5 off-title">
          Leaderboard is hidden
        </h1>
        <p class="text-center off-subtitle px-6">
          Turn it on to see team rankings and performance for your workspace.
        </p>
        <div class="d-flex align-center justify-center">
          <v-switch
            v-model="localSeeScore"
            class="mt-4"
            inset
            label="Show leaderboard"
            color="primary"
            @change="ScoreVisible"
          />
        </div>
      </v-card>
    </v-container>
  </v-container>
</template>

<script>
import Leader from "../components/leader.vue";
import Vue from "vue";
import VueConfetti from "vue-confetti";
import { mapGetters } from "vuex";
const _ = require("lodash");
import moment from "moment-timezone";

Vue.use(VueConfetti);

export default {
  components: { Leader },
  data() {
    return {
      showHeroBanner: true,
      tabs: "topCaller",
      accounts: [],
      time: "Daily",
      timeoptions: ["Daily", "Weekly", "Monthly"],
      dateRange: [],
      callerActivity: [],
      graphByAccount: [],
      finalData: [],
      localSeeScore: false,
      rankingDispos: [],
    };
  },
  computed: {
    ...mapGetters(["dispoList", "selectedTeam", "teams", "ENDPOINT", "user"]),
    seeScore() {
      return this.$store.getters.user.seeScore;
    },
    dispoOptions() {
      return _.filter(this.dispoList || [], (d) => !d.system);
    },
    useDispoRanking() {
      return this.rankingDispos && this.rankingDispos.length > 0;
    },
    rankingDispoLabel() {
      if (!this.useDispoRanking) {
        return "Calls";
      }
      if (this.rankingDispos.length === 1) {
        return this.rankingDispos[0];
      }
      return `${this.rankingDispos.length} dispositions`;
    },
  },
  watch: {
    localSeeScore(newVal) {
      if (newVal) {
        this.initSet();
      }
    },
    tabs() {
      this.sortFinalData();
    },
  },
  created() {
    this.showHeroBanner =
      localStorage.getItem("leaderboardHeaderDismissed") !== "true";
    this.loadRankingDispos();
    this.localSeeScore = this.seeScore;
    if (this.localSeeScore) {
      this.initSet();
    }
  },
  methods: {
    dismissHeader() {
      this.showHeroBanner = false;
      localStorage.setItem("leaderboardHeaderDismissed", "true");
    },
    ScoreVisible() {
      var payload = {};
      payload.seeScore = this.localSeeScore;
      this.$http
        .patch(
          this.$store.state.ENDPOINT + "/accounts/" + this.$store.state.user.id,
          payload,
        )
        .then(() => {
          var user = this.$store.getters.user;
          user.seeScore = this.localSeeScore;
          this.$store.commit("setUser", user);
        })
        .catch(() => {
          console.log("Error in patching");
        });
    },
    async initSet() {
      this.setDates();
      await this.getAccountsByTeam();
      await this.getAccountId();
      this.start();
      setTimeout(this.stop, 3000);
    },
    start() {
      this.$confetti.start();
    },

    stop() {
      this.$confetti.stop();
    },
    async getAccountsByTeam() {
      try {
        const self = this;
        let teamIds = [self.selectedTeam];

        if (self.selectedTeam == "allTeams") {
          teamIds = _.map(
            _.filter(self.teams, (t) => {
              return t.id != "allTeams";
            }),
            "id",
          );
        }

        let accountsRespo = await self.$http.post(
          `${self.ENDPOINT}/accounts/fetch-v2`,
          {
            teams: teamIds,
          },
        );

        self.accounts = accountsRespo.body;
      } catch (err) {
        console.log("Error: In getAccountsByTeam", err);
      }
    },
    setDates() {
      let startDate = moment().format("YYYY-MM-DD");
      let endDate = moment().format("YYYY-MM-DD");

      if (this.time == "Weekly") {
        startDate = moment()
          .subtract(7, "days")
          .format("YYYY-MM-DD");
      } else if (this.time == "Monthly") {
        startDate = moment()
          .subtract(30, "days")
          .format("YYYY-MM-DD");
      }

      this.dateRange = [startDate, endDate];
    },
    async onDateChange() {
      await this.setDates();
      await this.getAccountId();
      this.start();
      setTimeout(this.stop, 3000);
    },
    loadRankingDispos() {
      try {
        const raw = localStorage.getItem("leaderboardRankingDispos");
        if (raw) {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) {
            this.rankingDispos = parsed;
          }
        }
      } catch (e) {
        this.rankingDispos = [];
      }
    },
    saveRankingDispos() {
      try {
        localStorage.setItem(
          "leaderboardRankingDispos",
          JSON.stringify(this.rankingDispos || []),
        );
      } catch (e) {
        void e;
      }
    },
    dispoSelectionTitle(item) {
      if (item == null) return "";
      if (typeof item === "string") return item;
      return item.title || item.text || String(item);
    },
    removeRankingDispo(item) {
      const title = this.dispoSelectionTitle(item);
      this.rankingDispos = (this.rankingDispos || []).filter(
        (d) => d !== title,
      );
      this.onRankingDisposChange();
    },
    async onRankingDisposChange() {
      this.saveRankingDispos();
      await this.getAccountId();
      this.start();
      setTimeout(this.stop, 3000);
    },
    sumDispoCounts(stat, dispos) {
      if (!stat) {
        return 0;
      }
      if (!dispos || !dispos.length) {
        return Number(stat.count) || 0;
      }
      const status = stat.status || [];
      if (!status.length) {
        return Number(stat.count) || 0;
      }
      const selected = new Set(dispos);
      return _.sumBy(status, (row) =>
        selected.has(row._id) ? Number(row.count) || 0 : 0,
      );
    },
    sortFinalData() {
      let orderField = "count";
      if (this.tabs === "topActive") {
        orderField = "timeSpentOnCall";
      } else if (this.useDispoRanking) {
        orderField = "dispoScore";
      }
      this.finalData = _.orderBy(this.finalData, [orderField], ["desc"]);
    },
    async fetchGraphStats() {
      if (!this.useDispoRanking) {
        this.graphByAccount = [];
        return;
      }
      const userIds = _.map(this.accounts, "id");
      if (!userIds.length) {
        this.graphByAccount = [];
        return;
      }
      const resp = await this.$http.post(
        `${this.$store.state.ENDPOINT}/logs/v2/graph`,
        {
          range: this.dateRange,
          lists: [],
          users: userIds,
          dispos: this.rankingDispos,
        },
      );
      this.graphByAccount = resp.body || [];
    },
    async getAccountId() {
      try {
        const userIds = _.map(this.accounts, "id");
        if (!userIds.length) {
          this.finalData = [];
          return;
        }

        const activityResp = await this.$http.post(
          `${this.$store.state.ENDPOINT}/logs/v2/filterByDays`,
          {
            users: userIds,
            dates: this.dateRange,
          },
        );

        this.callerActivity = activityResp.body || [];
        await this.fetchGraphStats();

        const graphByAccountId = {};
        _.each(this.graphByAccount, (row) => {
          const id =
            row._id && row._id.accountsId != null
              ? String(row._id.accountsId)
              : null;
          if (id) {
            graphByAccountId[id] = row;
          }
        });

        this.finalData = [];
        this.accounts.forEach((a) => {
          const accountId = String(a.id);
          let activities = _.filter(this.callerActivity, (c) => {
            return accountId === String(c._id.accountsId);
          });
          let presentDates = _.uniq(
            _.map(
              _.filter(activities, (activity) => Number(activity.count) > 0),
              (activity) => moment(activity._id.day).format("YYYY-MM-DD"),
            ),
          );

          let lA = {
            id: a.id,
            name: a.name,
            role: a.role,
            picture: a.picture,
            pictureId: a.pictureId,
            presentCount: presentDates.length,
            dispoScore: 0,
          };

          if (!activities.length) {
            lA.count = 0;
            lA.duration = 0;
            lA.timeSpentOnCall = 0;
            lA.connectedCalls = 0;
            lA.notConnectedCalls = 0;
          } else {
            lA.count = _.sumBy(
              activities,
              (activity) => Number(activity.count) || 0,
            );
            lA.duration = _.sumBy(
              activities,
              (activity) => Number(activity.duration) || 0,
            );
            lA.timeSpentOnCall = Math.round(
              _.sumBy(
                activities,
                (activity) => Number(activity.timeSpentOnCall) || 0,
              ) /
                (1000 * 60),
            );
            lA.connectedCalls = _.sumBy(
              activities,
              (activity) => Number(activity.connectedCalls) || 0,
            );
            lA.notConnectedCalls = lA.count - lA.connectedCalls;
          }

          if (this.useDispoRanking) {
            const graphRow = graphByAccountId[accountId];
            lA.dispoScore = this.sumDispoCounts(
              graphRow,
              this.rankingDispos,
            );
            const status = (graphRow && graphRow.status) || [];
            this.rankingDispos.forEach((dispoTitle, index) => {
              const match = _.find(status, (row) => row._id === dispoTitle);
              lA[`dispoCol${index}`] = match ? Number(match.count) || 0 : 0;
            });
          }

          this.finalData.push(lA);
        });

        this.sortFinalData();
      } catch (e) {
        console.log("Error: Get All Account", e);
      }
    },
  },
};
</script>

<style scoped>
.game-container {
  background-color: var(--v-background-base, #f5f5f5);
  min-height: 100vh;
}

.theme--dark .game-container {
  background-color: #121212 !important;
}

.header-section {
  background: linear-gradient(135deg, #37cf37 0%, #2ba82b 100%);
  padding: 24px 20px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(55, 207, 55, 0.25);
  position: relative;
  overflow: hidden;
}

.header-section::after {
  content: "";
  position: absolute;
  top: -40%;
  right: -10%;
  width: 280px;
  height: 280px;
  background: radial-gradient(
    circle,
    rgba(255, 255, 255, 0.12) 0%,
    transparent 70%
  );
  pointer-events: none;
}

@media (min-width: 960px) {
  .header-section {
    padding: 28px 32px;
  }
}

.header-icon-wrapper {
  width: 64px;
  height: 64px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.page-title {
  color: white;
  font-size: 28px;
  font-weight: 600;
  margin: 0;
  letter-spacing: -0.5px;
}

.page-subtitle {
  color: rgba(255, 255, 255, 0.95);
  font-size: 14px;
  margin: 4px 0 0 0;
  font-weight: 400;
}

.feature-chip {
  background: rgba(255, 255, 255, 0.25) !important;
  color: white !important;
  border-color: rgba(255, 255, 255, 0.4) !important;
  backdrop-filter: blur(10px);
  font-weight: 500;
}

.feature-pills-container {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.feature-pill {
  background: rgba(255, 255, 255, 0.15);
  color: white;
  padding: 10px 20px;
  border-radius: 24px;
  font-size: 13px;
  font-weight: 500;
  display: flex;
  align-items: center;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
  cursor: default;
}

.feature-pill:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.leader-page {
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
}

/* Same width for hero + main card (avoid v-row negative margin stretch) */
.leader-stack-block {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.header-close-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 3;
  background: rgba(0, 0, 0, 0.12) !important;
}

.header-close-btn:hover {
  background: rgba(0, 0, 0, 0.22) !important;
}

.header-top-row {
  padding-right: 36px;
}

.header-title-col {
  min-width: 0;
}

.header-actions-col {
  display: flex;
  justify-content: flex-end;
}

.header-actions-inner {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  width: 100%;
}

.header-bottom {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: flex-end;
  gap: 12px 16px;
}

.header-bottom .feature-pills-container {
  flex: 1 1 200px;
  margin-top: 0;
}

.header-chips-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
  width: 100%;
}

.header-toggle-row {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  flex: 0 0 auto;
  margin-left: auto;
  min-height: 42px;
  min-width: 220px;
  padding: 8px 18px 8px 20px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.28);
  backdrop-filter: blur(10px);
  box-sizing: border-box;
}

@media (max-width: 599px) {
  .header-bottom {
    flex-direction: column;
    align-items: stretch;
  }

  .header-toggle-row {
    align-self: flex-end;
    min-width: 0;
    width: auto;
    max-width: 100%;
    padding: 8px 14px 8px 16px;
    gap: 12px;
  }
}

.header-toggle-label {
  color: rgba(255, 255, 255, 0.98);
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  line-height: 1.2;
  padding-right: 4px;
}

.header-switch {
  flex: 0 0 auto;
}

.header-switch >>> .v-input--selection-controls {
  margin-top: 0;
  padding-top: 0;
  flex: 0 0 auto;
}

.header-switch >>> .v-input__slot {
  margin-bottom: 0;
}

.header-switch >>> .v-input--selection-controls__input {
  margin-right: 0;
}

.header-switch >>> .v-input--switch .v-input--selection-controls__ripple {
  left: -8px;
}

.leader-main-card {
  border: 1px solid rgba(0, 0, 0, 0.06);
  overflow: visible !important;
}

.leader-main-card >>> .v-card__text {
  overflow: visible;
}

.theme--dark .leader-main-card {
  border-color: rgba(255, 255, 255, 0.08);
}

.leader-tabs >>> .v-tab {
  text-transform: none;
  font-weight: 500;
  letter-spacing: 0;
  font-size: 14px;
}

.leader-tabs >>> .v-tabs-bar {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.theme--dark .leader-tabs >>> .v-tabs-bar {
  border-bottom-color: rgba(255, 255, 255, 0.08);
}

.leader-tab-items {
  background: transparent !important;
}

.leader-filters {
  background: #fafafa;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.theme--dark .leader-filters {
  background: #1a1a1a;
  border-bottom-color: rgba(255, 255, 255, 0.08);
}

.leader-filter-col {
  display: flex;
  flex-direction: column;
}

.filter-field-hint {
  margin: 4px 0 0;
  padding: 0 12px;
  min-height: 20px;
  font-size: 12px;
  line-height: 1.25;
  color: rgba(0, 0, 0, 0.54);
}

.theme--dark .filter-field-hint {
  color: rgba(255, 255, 255, 0.54);
}

.filter-field-hint--spacer {
  visibility: hidden;
  user-select: none;
}

.filter-field--control >>> .v-input__control {
  min-height: 40px;
}

.filter-field--control >>> .v-input__slot {
  min-height: 40px !important;
  max-height: 40px !important;
  align-items: center !important;
}

.filter-field--control >>> .v-text-field__slot,
.filter-field--control >>> .v-select__slot {
  align-items: center;
  min-height: 40px;
}

.filter-field--dispos >>> .v-input__prepend-inner {
  align-self: center !important;
  margin-top: 0 !important;
  padding-right: 4px;
}

.filter-field--dispos >>> .v-select__slot {
  align-items: center;
}

.filter-field--dispos >>> .v-select__selections {
  flex: 1 1 auto;
  flex-wrap: nowrap;
  overflow: hidden;
  align-items: center;
  gap: 6px;
  min-width: 0;
  margin: 0;
  padding: 0 0 0 2px;
  height: 24px;
}

.filter-field--dispos >>> .filter-dispo-chip.v-chip {
  flex: 0 1 auto;
  max-width: calc(50% - 48px);
  height: 24px !important;
  margin: 0 !important;
  padding: 0 2px 0 8px !important;
  border-radius: 12px !important;
  font-size: 11px !important;
  font-weight: 600;
  letter-spacing: 0.02em;
  background: #e8eaed !important;
  color: #37474f !important;
}

.filter-field--dispos >>> .filter-dispo-chip .v-chip__content {
  display: inline-flex !important;
  align-items: center;
  min-width: 0;
  padding: 0 !important;
}

.filter-dispo-chip__text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
  line-height: 1.2;
}

.filter-field--dispos >>> .filter-dispo-chip .v-chip__close {
  flex-shrink: 0;
  margin-left: 2px !important;
  margin-right: 2px !important;
  opacity: 0.65;
}

.filter-field--dispos >>> .filter-dispo-chip .v-chip__close:hover {
  opacity: 1;
}

.filter-dispo-more {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 8px;
  border-radius: 12px;
  background: rgba(55, 71, 79, 0.1);
  font-size: 12px;
  font-weight: 600;
  color: #546e7a;
  white-space: nowrap;
  line-height: 1;
}

.theme--dark .filter-field--dispos >>> .filter-dispo-chip.v-chip {
  color: #eceff1 !important;
  background: #455a64 !important;
}

.theme--dark .filter-dispo-more {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.72);
}

.leaderboard-off {
  background-color: var(--v-background-base, #f5f5f5);
  min-height: 100vh;
}

.theme--dark .leaderboard-off {
  background-color: #121212 !important;
}

.off-title {
  color: #2ba82b;
}

.theme--dark .off-title {
  color: #37cf37;
}

.off-subtitle {
  font-size: 14px;
  opacity: 0.65;
  max-width: 360px;
  margin: 0 auto;
}

.modern-card {
  background: white;
  border-radius: 12px;
  position: relative;
  overflow: hidden;
}

.theme--dark .modern-card {
  background: #1e1e1e;
}

.card-header-green {
  height: 4px;
  background: linear-gradient(90deg, #37cf37 0%, #2ba82b 100%);
  width: 100%;
}

.customBorderRadius {
  border-radius: 12px !important;
}


@media (max-width: 960px) {
  .page-title {
    font-size: 22px;
  }

  .header-icon-wrapper {
    width: 48px;
    height: 48px;
    border-radius: 12px;
  }

  .header-icon-wrapper .v-icon {
    font-size: 24px !important;
  }

  .feature-pills-container {
    gap: 8px;
  }

  .feature-pill {
    padding: 8px 16px;
    font-size: 12px;
  }
}
</style>
